import type { IContentItem, ISeoArticle, ITextBlock, ITextBlocks } from '~~/types/local_types';
import type {
  ICmsArticlesContent,
  ICmsArticlesContentEmbeds,
  ICmsArticlesContentImages,
  ICmsContentEmbeds,
} from '~~/types/_cms-types';

/**
 * Map the content to the correct format to be used in the SEO meta tags
 * @param item
 */
export default function useMapArticle(item: ISeoArticle): IContentItem {
  let articleContent = {};

  /**
   * Get the main image
   * @param mainImage
   */
  const getMainImage = (mainImage: ICmsArticlesContentImages[] | undefined) => {
    const imageObject = mainImage?.map((item) => item.contentImagesId)[0];
    return imageObject?.image;
  };

  /**
   * Get and map the main video
   * @param mainEmbed
   */
  const getMainVideo = (mainEmbed: ICmsArticlesContentEmbeds[] | undefined) => {
    const embedObject = mainEmbed?.map((item) => {
      return item.contentEmbed;
    })[0];
    if (!embedObject || embedObject?.embed?.oembed?.type !== 'video') return undefined;
    return embedObject;
  };

  const getContentVideos = (content?: ICmsArticlesContent[]): ICmsContentEmbeds[] => {
    return (content
      ?.filter((item) => item.collection === 'content_embeds')
      .map((item) => {
        return item.item as unknown as ICmsContentEmbeds;
      }) || []) as ICmsContentEmbeds[];
  };

  /**
   * Get the intro from the heading content
   * @param intro
   */
  const getIntro = (intro: ITextBlocks) => {
    return mapAndSanitizeTextBlocks(intro?.blocks);
  };

  /**
   * Map the text blocks to a string and sanitize it
   * @param blocks
   */
  const mapAndSanitizeTextBlocks = (blocks: ITextBlock[] | undefined): string | undefined => {
    if (!blocks?.length) {
      return;
    }
    return sanitizeHTML(useMapTextBlocks(blocks) || '', {
      USE_PROFILES: { html: false },
    });
  };

  const tagsTitles = item.tags?.map(({ tagsId }) => tagsId?.title) || [];
  const themesTitles = item.themes?.map(({ themesId }) => themesId?.title) || [];
  const dossierTitles = item.dossiers?.map(({ dossiersId }) => dossiersId?.title) || [];
  const dossiers = item.dossiers?.map(({ dossiersId }) => dossiersId) || [];
  const themes = item.themes?.map(({ themesId }) => themesId) || [];
  const videos = getContentVideos(item.content);

  // Only add the fields that are specific for an article when type is not page
  if (item.articleType !== 'page') {
    articleContent = {
      publishTime: item.publishTime,
      adjustTime: item.adjustTime,
      tagsTitles,
      themesTitles,
      dossiers,
      themes,
      keywords: [...themesTitles, ...dossierTitles, ...tagsTitles], // Combine all
      videos,
    };
  }

  const socialDescription =
    mapAndSanitizeTextBlocks(item.socialDescriptionBlocks) ||
    item.socialDescription ||
    getIntro(item.intro as ITextBlocks);
  const socialImage = item.socialImage || getMainImage(item.mainImage);
  const socialVideo = getMainVideo(item.mainEmbed);

  const socialContent = {
    socialTitle: item.socialTitle || item.title,
    socialDescription,
    socialImage,
    socialVideo,
    publishTime: item.publishTime,
  };

  return {
    id: item.id,
    ...articleContent,
    ...socialContent,
  };
}
